import React from 'react';
import { MapPin, Phone, Clock } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { wpMedia } from '../utils/wpMedia';

export const ServiceArea = () => {
  const location = useLocation();
  const scheduleHref = location.pathname === '/' || location.pathname === '/aquify' || location.pathname === '/aquify/' ? '#water-evaluation-form' : '/contact/#water-evaluation-form';
  const nyAreas = [
    'Nassau County',
    'Suffolk County',
    'New York City',
    'Westchester',
  ];

  const flAreas = ['Palm Beach County', 'Broward County'];

  return (
    <section className="py-20 relative overflow-hidden bg-slate-50">
      <style dangerouslySetInnerHTML={{
        __html: `
        .service-map-bg {
          position: absolute;
          inset: 0 auto 0 0;
          width: 100%;
          background-image: url(${process.env.PUBLIC_URL}/aquify-service-area-map.png);
          background-repeat: no-repeat;
          background-size: contain;
          background-position: left center;
          opacity: 0.12;
          pointer-events: none;
          -webkit-mask-image: linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 15%, rgba(0,0,0,1) 50%, transparent 100%);
          mask-image: linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 15%, rgba(0,0,0,1) 50%, transparent 100%);
        }
        @media (min-width: 768px) {
          .service-map-bg {
            width: 85%;
            opacity: 0.18;
            -webkit-mask-image: linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 15%, rgba(0,0,0,1) 60%, transparent 100%);
            mask-image: linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 15%, rgba(0,0,0,1) 60%, transparent 100%);
          }
        }
        @media (min-width: 1024px) {
          .service-map-bg {
            width: 72%;
            opacity: 0.6;
            -webkit-mask-image: linear-gradient(to right, transparent 0%, rgba(0,0,0,0.9) 10%, rgba(0,0,0,1) 40%, rgba(0,0,0,0.1) 85%, transparent 100%);
            mask-image: linear-gradient(to right, transparent 0%, rgba(0,0,0,0.9) 10%, rgba(0,0,0,1) 40%, rgba(0,0,0,0.1) 85%, transparent 100%);
          }
        }
      `}} />
      {/* ── Background Map Image ── */}
      <div
        className="service-map-bg z-0"
        aria-hidden="true"
      />
      {/* ── Gradient Fallback (For browsers lacking mask support) ── */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to right, #f8fafc 0%, rgba(248, 250, 252, 0) 15%, rgba(248, 250, 252, 0) 40%, #f8fafc 80%)'
        }}
        aria-hidden="true"
      />

      {/* ── Content Container ── */}
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Proudly Serving <span className="text-[#1571BA]">Long Island &amp; South Florida</span>
            </h2>
            <p className="text-xl text-gray-800 font-medium mb-8 leading-relaxed">
              As a locally owned and operated business, we understand the unique water challenges homeowners face across Long Island and South Florida.
              From hard water to iron content, we have the expertise and solutions for your specific area.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-4 text-lg">
                <div className="bg-[#E3F2FD] p-3 rounded-full">
                  <MapPin className="h-6 w-6 text-[#1571BA]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Service Coverage</p>
                  <p className="text-gray-800 font-medium">Long Island, NYC, Westchester, Palm Beach County &amp; Broward County</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-lg">
                <div className="bg-[#E3F2FD] p-3 rounded-full">
                  <Clock className="h-6 w-6 text-[#1571BA]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Operating Hours</p>
                  <p className="text-gray-800 font-medium">Available 7 Days a Week</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-lg">
                <div className="bg-[#E3F2FD] p-3 rounded-full">
                  <Phone className="h-6 w-6 text-[#1571BA]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Contact Us</p>
                  <p className="text-gray-800 font-medium">516-729-9013</p>
                </div>
              </div>
            </div>

            <a
              href={scheduleHref}
              className="aq-primary-btn inline-flex items-center justify-center font-bold text-lg px-8 py-4 rounded-lg transform hover:scale-105 shadow-md"
            >
              Schedule Service
            </a>
          </div>

          <div>
            <div className="flex justify-center mb-8">
              <img
                src={wpMedia('2026/06/wqa-logo-scaled.png')}
                alt="Water Quality Association Member"
                className="max-w-[240px] w-full h-auto"
              />
            </div>

            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-lg shadow-xl border-2 border-[#50BAED]">
              {/* NY / Long Island areas */}
              <h3 className="text-2xl font-bold text-slate-900 mb-5">Areas We Serve</h3>
              <div className="grid grid-cols-2 gap-4">
                {nyAreas.map((area, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 bg-white rounded-lg hover:bg-[#E3F2FD] transition-colors duration-200 border border-slate-100"
                  >
                    <MapPin className="h-5 w-5 text-[#1571BA] flex-shrink-0" />
                    <span className="font-medium text-slate-900">{area}</span>
                  </div>
                ))}
              </div>

              {/* South Florida expansion */}
              <div className="mt-8 pt-7 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#50BAED]"></span>
                  <h4 className="text-lg font-bold text-slate-900">Now Serving South Florida</h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {flAreas.map((area, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-4 bg-white rounded-lg hover:bg-[#E3F2FD] transition-colors duration-200 border border-slate-100"
                    >
                      <MapPin className="h-5 w-5 text-[#1571BA] flex-shrink-0" />
                      <span className="font-medium text-slate-900">{area}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="mt-6 text-center text-gray-700 italic font-medium">
                Don't see your area? Call us - we may still be able to help!
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceArea;